import { Box, Table, Text } from '@mantine/core';
import { mantineTheme } from 'init/mantine';
import { i18nKeys, useTranslation } from 'locales';
import { useMemo } from 'react';
import { capitalize } from 'remeda';
import type { ApplicationLocale } from 'types';
import type { CohortGrid } from './collection-cohorts-grid';
import { toRampStep } from './collection-cohorts-ramp';
import { formatPercentage } from './percentage';

const CELL_FONT_SIZE = 14;
const CELL_PADDING = 6;
const PILL_PADDING_Y = 9;
const PILL_PADDING_X = 8;
const STACKED_LEADING = 1.2;

const GRID_GUTTER = 16;
const WINDOW_FLOOR_WIDTH = 84;

const SHRINK_TO_CONTENT = {
  whiteSpace: 'nowrap',
  verticalAlign: 'middle',
  width: '1px',
} as const;
const BEFORE_GRID = { ...SHRINK_TO_CONTENT, paddingRight: GRID_GUTTER } as const;
const AFTER_GRID = { ...SHRINK_TO_CONTENT, paddingLeft: GRID_GUTTER } as const;

const toWindowColumn = (windowCount: number) =>
  ({
    width: `${100 / windowCount}%`,
    minWidth: WINDOW_FLOOR_WIDTH,
    verticalAlign: 'middle',
  }) as const;

type CollectionCohortsTableProps = {
  grid: CohortGrid;
  currency: string;
};

const toCohortLabel = (period: string, locale: ApplicationLocale) =>
  capitalize(
    new Date(period).toLocaleDateString(locale, {
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    }),
  );

export const CollectionCohortsTable = ({ grid, currency }: CollectionCohortsTableProps) => {
  const { t, currentLang } = useTranslation();

  const money = useMemo(
    () =>
      new Intl.NumberFormat(currentLang, {
        style: 'currency',
        currency,
        maximumFractionDigits: 0,
      }),
    [currentLang, currency],
  );

  if (grid.rows.length === 0) {
    return (
      <Text size="sm" c="gray.6">
        {t(i18nKeys.ANALYTICS.COLLECTION_COHORTS.EMPTY)}
      </Text>
    );
  }

  const windowColumn = toWindowColumn(grid.windows.length);

  const headerProps = {
    c: 'gray.6',
    bg: 'transparent',
    pt: CELL_PADDING,
    pb: CELL_PADDING,
    fz: CELL_FONT_SIZE,
  } as const;

  return (
    <Box style={{ overflowX: 'auto' }}>
      <Table verticalSpacing={CELL_PADDING} horizontalSpacing={CELL_PADDING} fz={CELL_FONT_SIZE}>
        <Table.Thead bg="transparent" style={{ borderBottom: 'none' }}>
          <Table.Tr>
            <Table.Th scope="col" {...headerProps} style={SHRINK_TO_CONTENT}>
              {t(i18nKeys.ANALYTICS.COLLECTION_COHORTS.COHORT)}
            </Table.Th>
            <Table.Th scope="col" ta="right" {...headerProps} style={BEFORE_GRID}>
              {t(i18nKeys.ANALYTICS.COLLECTION_COHORTS.BILLED)}
            </Table.Th>
            {grid.windows.map(window => (
              <Table.Th key={window} scope="col" ta="center" {...headerProps} style={windowColumn}>
                {t(i18nKeys.ANALYTICS.COLLECTION_COHORTS.WINDOW, { days: window })}
              </Table.Th>
            ))}
            <Table.Th scope="col" ta="left" {...headerProps} style={AFTER_GRID}>
              {t(i18nKeys.ANALYTICS.COLLECTION_COHORTS.COLLECTED)}
            </Table.Th>
            <Table.Th scope="col" ta="right" {...headerProps} style={SHRINK_TO_CONTENT}>
              {t(i18nKeys.ANALYTICS.COLLECTION_COHORTS.UNPAID)}
            </Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {grid.rows.map(row => (
            <Table.Tr
              key={row.period}
              aria-label={t(i18nKeys.ANALYTICS.COLLECTION_COHORTS.INVOICE_COUNT, {
                count: row.invoiceCount,
              })}
            >
              <Table.Th scope="row" fw={600} {...headerProps} c="black.0" style={SHRINK_TO_CONTENT}>
                {toCohortLabel(row.period, currentLang)}
              </Table.Th>
              <Table.Td ta="right" fz={CELL_FONT_SIZE} style={BEFORE_GRID}>
                {money.format(row.amount)}
              </Table.Td>
              {row.cells.map((cell, index) => (
                <Table.Td key={`${row.period}-${grid.windows[index]}`} p={2}>
                  {cell === null ? null : (
                    <Box
                      ta="center"
                      style={{
                        padding: `${PILL_PADDING_Y}px ${PILL_PADDING_X}px`,
                        fontSize: CELL_FONT_SIZE,
                        lineHeight: STACKED_LEADING,
                        fontWeight: 600,
                        borderRadius: 6,
                        backgroundColor: toRampStep(cell.share).fill,
                        color: toRampStep(cell.share).ink,
                      }}
                    >
                      {formatPercentage(cell.share)}
                    </Box>
                  )}
                </Table.Td>
              ))}
              <Table.Td ta="left" fw={600} fz={CELL_FONT_SIZE} style={AFTER_GRID} c="black.0">
                {formatPercentage(row.collectedShare)}
              </Table.Td>
              <Table.Td
                ta="right"
                fz={CELL_FONT_SIZE}
                style={SHRINK_TO_CONTENT}
                c={row.unpaidAmount > 0 ? 'coral.8' : mantineTheme.colors.gray[5]}
              >
                {money.format(row.unpaidAmount)}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Box>
  );
};
